import React, { useState, useCallback } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

interface UploadModelDialogProps {
    open: boolean;
    onClose: () => void;
    onModelAdded: () => void;
}

const UploadModelDialog: React.FC<UploadModelDialogProps> = ({ open, onClose, onModelAdded }) => {
    const [file, setFile] = useState<File | null>(null);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        const selectedFile = acceptedFiles[0];
        if (selectedFile) {
            setFile(selectedFile);
        } else {
            alert("Please upload a valid Excel file (.xls or .xlsx)");
            setFile(null);
        }
    }, []);

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: {
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [],
            "application/vnd.ms-excel": [],
        },
        multiple: false,
    });

    const handleSubmit = () => {
        if (!file) {
            alert("Please select an Excel file to upload");
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        const token: string | null = localStorage.getItem("token");
        if (!token) return;

        fetch(`${process.env.REACT_APP_API_BASE_URL}/vehicleModel/vehicleModelUpload`, {
            method: "POST",
            headers: { "x-api-key": token },
            body: formData,
        })
            .then((response) => response.json())
            .then(() => {
                onModelAdded();
                onClose();
            })
            .catch((error) => console.error("Error uploading file:", error));
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    backgroundColor: "#121212",
                    border: "2px solid green",
                    borderRadius: "12px",
                    padding: "16px",
                    width: "400px",
                },
            }}
        >
            <DialogTitle sx={{ color: "green", textAlign: "center" }}>Upload Excel File</DialogTitle>
            <DialogContent>
                <Box
                    {...getRootProps()}
                    sx={{
                        border: "2px dashed green",
                        padding: "20px",
                        textAlign: "center",
                        cursor: "pointer",
                        backgroundColor: "#1a1a1a",
                        color: "white",
                        borderRadius: "10px",
                        "&:hover": {
                            backgroundColor: "#222",
                        },
                    }}
                >
                    <input {...getInputProps()} />
                    <CloudUploadIcon sx={{ fontSize: 40, color: "green" }} />
                    <Typography>Drag & Drop your Excel file here</Typography>
                    <Typography variant="body2">or click to select a file</Typography>
                </Box>
                {file && (
                    <Typography sx={{ marginTop: 2, color: "green", textAlign: "center" }}>
                        Selected File: <b>{file.name}</b>
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center" }}>
                <Button onClick={onClose} sx={{ color: "white", border: "1px solid green" }}>
                    Cancel
                </Button>
                <Button onClick={handleSubmit} sx={{ backgroundColor: "green", color: "black" }}>
                    Upload
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UploadModelDialog;
